import { clip, type Clip } from '../clip.ts';
import { inOut, inQuad, outExpo, outQuad, phase, settle, span, wave } from '../curves.ts';
import { apply, timeline, type KeyPose } from '../pose.ts';

/*
 * Prestado (models/prestado.ts): too tall, too thin, hunched. Idle: it sways from the hips and tilts its faceless
 * head as if listening, fingers twitching. Walk: a long, loping stride, the arms swinging loose past the knees and
 * the head held level ahead. Attack: it rears up and rakes down with both hands at ~0.3 s. Hit, stun (loop), die
 * (it folds at the knees and crumples face down).
 *
 * Conventions as in human.ts: limbs hang along −Y, so −X swings an arm or leg forward; +X on a shin bends the knee
 * back; −X on a forearm bends the elbow; +X on the trunk leans it forward; +Z on the right arm lifts it out.
 */

type B = 'root' | 'hips' | 'chest' | 'head' | 'armL' | 'foreL' | 'armR' | 'foreR' | 'legL' | 'shinL' | 'legR' | 'shinR';
type P = KeyPose<B>;

/** Hunched: chest pitched forward, head craned out and level, arms hanging loose, knees soft. */
const ST: P = {
  root: [0, 0, 0, 0, -0.04, 0],
  hips: [0.06, 0, 0],
  chest: [0.28, 0, 0],
  head: [-0.32, 0, 0],
  armL: [-0.12, 0, -0.08],
  armR: [-0.12, 0, 0.08],
  foreL: [-0.18, 0, 0],
  foreR: [-0.18, 0, 0],
  legL: [-0.12, 0, -0.03],
  legR: [-0.12, 0, 0.03],
  shinL: [0.22, 0, 0],
  shinR: [0.22, 0, 0],
};

const idle = (): Clip<B> =>
  clip<B>('idle', 3.6, true, (b, t) => {
    const p = phase(t, 3.6);
    apply(b, ST);
    b.hips.rot(0, 0.04 * wave(p), 0.05 * wave(p));
    b.chest.rot(0.03 * wave(p, 0.2, 2), 0.05 * wave(p, 0.1), 0);
    // It listens: the head turns and tilts, holds, comes back.
    const look = wave(p, 0.3);
    b.head.rot(0.06 * wave(p, 0.1, 2), 0.35 * look, 0.28 * look);
    b.foreL.rot(-0.1 * Math.max(0, wave(p, 0.6, 3)) ** 3);
    b.foreR.rot(-0.1 * Math.max(0, wave(p, 0.1, 3)) ** 3);
    b.root.pos(0, 0.01 * wave(p, 0, 2), 0);
  });

const walk = (dur = 0.9): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const p = phase(t, dur);
    apply(b, ST);
    const s = wave(p);
    // Long strides, the knee lifting high on the swing.
    b.legL.rot(-0.55 * s);
    b.legR.rot(0.55 * s);
    b.shinL.rot(0.5 * Math.max(0, -Math.cos(p * Math.PI * 2 - 0.4)) ** 1.5);
    b.shinR.rot(0.5 * Math.max(0, Math.cos(p * Math.PI * 2 - 0.4)) ** 1.5);
    // Arms swing loose and late, opposite the legs.
    b.armL.rot(0.42 * wave(p, -0.08), 0, 0);
    b.armR.rot(-0.42 * wave(p, -0.08), 0, 0);
    b.foreL.rot(-0.2 * Math.max(0, wave(p, -0.15)));
    b.foreR.rot(-0.2 * Math.max(0, -wave(p, -0.15)));
    b.hips.rot(0, 0.12 * s, 0.05 * s);
    b.chest.rot(0.04 * wave(p, 0.1, 2), -0.16 * s, -0.04 * s);
    // The head stays level and dead ahead.
    b.head.rot(-0.04 * wave(p, 0.1, 2), 0.14 * s, 0.03 * s);
    b.root.pos(0, 0.035 * Math.abs(wave(p, 0.25)) - 0.04, 0);
  });

const attack = (): Clip<B> =>
  clip<B>('attack', 0.75, false, (b, t) => {
    const reared: P = { ...ST, chest: [-0.2, 0, 0], head: [-0.1, 0, 0], armL: [-2.6, 0, -0.4], armR: [-2.6, 0, 0.4], foreL: [-0.6, 0, 0], foreR: [-0.6, 0, 0], legL: [-0.05, 0, -0.03], legR: [-0.05, 0, 0.03], shinL: [0.1, 0, 0], shinR: [0.1, 0, 0], root: [0, 0, 0, 0, 0.05, -0.05] };
    const rake: P = { ...ST, chest: [0.65, 0, 0], head: [-0.5, 0, 0], armL: [-0.4, 0, -0.15], armR: [-0.4, 0, 0.15], foreL: [-0.1, 0, 0], foreR: [-0.1, 0, 0], legL: [-0.4, 0, -0.03], legR: [0.2, 0, 0.03], shinL: [0.45, 0, 0], shinR: [0.3, 0, 0], root: [0, 0, 0, 0, -0.1, 0.18] };
    timeline(b, t, [
      [0, ST],
      [0.22, reared, outQuad],
      [0.32, rake, inQuad],
      [0.48, rake],
      [0.75, ST, inOut],
    ], { head: 0.03, foreL: 0.03, foreR: 0.04 });
  });

const hit = (): Clip<B> =>
  clip<B>('hit', 0.4, false, (b, t) => {
    apply(b, ST);
    const k = settle(t, 2.5, 8) * span(t, 0, 0.03, outQuad);
    b.root.pos(0, 0, -0.08 * k);
    b.chest.rot(-0.4 * k, 0.2 * k, 0);
    b.head.rot(-0.4 * k, -0.3 * k, 0.2 * k);
    b.armL.rot(-0.5 * k, 0, -0.3 * k);
    b.armR.rot(-0.5 * k, 0, 0.3 * k);
  });

const stun = (): Clip<B> =>
  clip<B>('stun', 1.6, true, (b, t) => {
    const p = phase(t, 1.6);
    apply(b, ST);
    b.root.rot(0, 0, 0.07 * wave(p));
    b.chest.rot(0.2, 0.06 * wave(p, 0.2), 0);
    b.head.rot(0.3, 0.5 * wave(p, 0.1), 0.2 * wave(p, 0.25));
    b.armL.rot(0.1, 0, -0.05);
    b.armR.rot(0.1, 0, 0.05);
  });

const die = (): Clip<B> =>
  clip<B>('die', 1.2, false, (b, t) => {
    const kneel: P = { ...ST, root: [0, 0, 0, 0, -0.42, 0.05], hips: [0.2, 0, 0], chest: [0.5, 0, 0.1], head: [0.3, 0.3, 0.2], legL: [-1.4, 0, -0.1], legR: [-1.3, 0, 0.1], shinL: [1.9, 0, 0], shinR: [1.8, 0, 0], armL: [-0.2, 0, -0.3], armR: [-0.1, 0, 0.4] };
    const down: P = { ...kneel, root: [1.25, 0, 0.1, 0, -0.85, 0.5], chest: [0.2, 0, 0.2], head: [0.1, 0.5, 0.3], armL: [-2.5, 0.2, -0.5], armR: [-2.3, -0.2, 0.6], foreL: [0.2, 0, 0], foreR: [0.3, 0, 0] };
    timeline(b, t, [
      [0, ST],
      [0.12, { ...ST, chest: [-0.4, 0, 0], head: [-0.6, 0, 0], armL: [-1.2, 0, -0.6], armR: [-1.2, 0, 0.6] }, outExpo],
      [0.5, kneel, inOut],
      [0.95, down, inQuad],
      [1.2, down],
    ], { head: 0.04, foreL: 0.04, foreR: 0.05 });
  });

export const CLIPS: Record<string, () => Clip[]> = { prestado: () => [idle(), walk(), attack(), hit(), stun(), die()] as Clip[] };
